import { useState } from "react";
import { Lock, Loader2, CheckCircle2, AlertCircle, KeyRound, Copy } from "lucide-react";
import { NexSysMark } from "./nexsys/ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RecoverPage() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token") || "";
  const bootstrap = params.get("bootstrap") === "1";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [key, setKey] = useState("");
  const [secret, setSecret] = useState("");
  const [issuedKey, setIssuedKey] = useState("");
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { setError("Passwords do not match."); return; }
    setBusy(true); setError("");
    try {
      const url = bootstrap ? `${API}/nexsys/auth/recovery/bootstrap/complete` : `${API}/nexsys/auth/recovery/complete`;
      const body = bootstrap
        ? { token, bootstrap_secret: secret.trim(), new_password: pw }
        : { token, new_password: pw, recovery_key: key.trim() };
      const r = await fetch(url, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setError(typeof d.detail === "string" ? d.detail : "Recovery failed."); setBusy(false); return; }
      if (bootstrap && d.recovery_key) setIssuedKey(d.recovery_key);
      setDone(true);
    } catch { setError("Network error. Please try again."); }
    setBusy(false);
  };

  const copyKey = async () => {
    try { await navigator.clipboard.writeText(issuedKey); setCopied(true); } catch { /* noop */ }
  };

  return (
    <div className="nx-root fixed inset-0 flex items-center justify-center bg-[#05070f] px-5" data-testid="recover-page">
      <div className="relative z-10 w-full max-w-[420px] rounded-2xl border border-white/10 bg-white/[0.03] p-7 backdrop-blur-xl">
        <div className="flex flex-col items-center text-center">
          <NexSysMark size={48} />
          <div className="mt-3 flex items-center gap-1.5 font-mono text-[10px] tracking-[0.25em] text-cyan-400/70">
            <Lock className="h-3 w-3" /> {bootstrap ? "OWNER BOOTSTRAP RECOVERY" : "ACCOUNT RECOVERY"}
          </div>
        </div>
        {done ? (
          <div className="mt-6 flex flex-col items-center gap-3 text-center" data-testid="recover-success">
            <CheckCircle2 className="h-8 w-8 text-emerald-400" />
            <p className="text-sm text-white/70">Password set privately. All prior sessions were signed out.</p>
            {issuedKey && (
              <div className="w-full rounded-xl border border-amber-400/30 bg-amber-500/10 p-3 text-left" data-testid="bootstrap-key-panel">
                <div className="flex items-center gap-1.5 font-mono text-[10px] tracking-widest text-amber-300">
                  <KeyRound className="h-3 w-3" /> RECOVERY KEY — SHOWN EXACTLY ONCE
                </div>
                <div className="mt-2 break-all rounded-lg bg-black/40 px-2.5 py-2 font-mono text-[12px] text-amber-100" data-testid="bootstrap-recovery-key">{issuedKey}</div>
                <button onClick={copyKey} className="mt-2 flex items-center gap-1.5 rounded-lg border border-amber-400/30 px-2.5 py-1.5 text-[11px] text-amber-200" data-testid="bootstrap-copy-key">
                  <Copy className="h-3 w-3" /> {copied ? "Copied" : "Copy key"}
                </button>
                <p className="mt-2 text-[11px] text-white/50">Store it offline now — it is never shown again and is required for any future Super Admin recovery. All prior recovery factors were revoked.</p>
              </div>
            )}
            <a href="/nexsys" className="rounded-xl bg-cyan-500 px-4 py-2 text-sm font-semibold text-black" data-testid="recover-to-login">Sign in</a>
          </div>
        ) : !token ? (
          <p className="mt-6 text-center text-sm text-white/50" data-testid="recover-no-token">
            Missing recovery token. Use the single-use link from your recovery email (valid 15 minutes).
          </p>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-3">
            {bootstrap && (
              <input value={secret} onChange={(e) => setSecret(e.target.value)} type="password" placeholder="Bootstrap secret (owner-set deployment value)" autoComplete="off"
                className="w-full rounded-xl border border-white/10 bg-black/30 px-3.5 py-2.5 text-sm text-white placeholder:text-white/30 outline-none focus:border-cyan-400/50" data-testid="bootstrap-secret" />
            )}
            <input value={pw} onChange={(e) => setPw(e.target.value)} type="password" placeholder="New password (min 8 chars)" autoComplete="new-password"
              className="w-full rounded-xl border border-white/10 bg-black/30 px-3.5 py-2.5 text-sm text-white placeholder:text-white/30 outline-none focus:border-cyan-400/50" data-testid="recover-password" />
            <input value={pw2} onChange={(e) => setPw2(e.target.value)} type="password" placeholder="Confirm new password" autoComplete="new-password"
              className="w-full rounded-xl border border-white/10 bg-black/30 px-3.5 py-2.5 text-sm text-white placeholder:text-white/30 outline-none focus:border-cyan-400/50" data-testid="recover-password2" />
            {!bootstrap && (
              <input value={key} onChange={(e) => setKey(e.target.value)} type="password" placeholder="Recovery key (required for Super Admin)" autoComplete="off"
                className="w-full rounded-xl border border-white/10 bg-black/30 px-3.5 py-2.5 text-sm text-white placeholder:text-white/30 outline-none focus:border-cyan-400/50" data-testid="recover-key" />
            )}
            {error && (
              <div className="flex items-center gap-2 rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2 text-[12px] text-red-300" data-testid="recover-error">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" /> {error}
              </div>
            )}
            <button type="submit" disabled={busy || pw.length < 8 || !pw2 || (bootstrap && !secret.trim())}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-cyan-500 px-4 py-2.5 text-sm font-semibold text-black disabled:opacity-50" data-testid="recover-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Set new password
            </button>
            <p className="text-center font-mono text-[10px] text-white/30">
              {bootstrap
                ? "Single-use · dual-factor (emailed link + owner-set secret) · permanently self-disables after success"
                : "Single-use link · security questions never authorize recovery alone · activity is audited"}
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
